import { BehaviorSubject, Observable, of as observableOf } from "rxjs";

import { catchError, map, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { ApicallOption, ApicallService } from "./apicall.service";
import { InfoService } from "./info.service";
import { FiatService } from "@main-services/currency/fiat.service";

import { Helper, parseList } from "@helpers/functions";

import {
	Country,
	Fee,
	Fiat,
	FiatLimit,
	Language,
	PaymentOption,
	PayoutAccount,
	PayoutProvider,
	Provider,
	Sepa
} from "@models";
import { InitService } from "./init.service";
import { ServiceNames } from "@models/service_enums";
import { TranslationService } from "./translation.service";
import { BFC_MIN_AMOUNT, TOP_COUNTRIES_LIST } from "@helpers/constants";
import { BpSelectOption } from "../components/bp-select/bp-select.component";
import { DarkModeService } from "@core-services/dark-mode.service";

// TODO: eslint fix
/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
	providedIn: "root"
})
export class MasterdataService {
	public name: string = ServiceNames.MASTER_DATA;

	private _payment_options: PaymentOption[] = [];
	private _payout_providers: PayoutProvider[] = [];
	private _languages: Language[] = [];
	private _countries: Country[] = [];
	private _citizenshipCountries: Country[] = [];
	private sepas: BehaviorSubject<Sepa[]> = new BehaviorSubject<Sepa[]>([]);
	private countryBpOptions: BpSelectOption[] = [];
	private languagesBpOptions: BpSelectOption[] = [];
	private bfc_deduction: number = 0;
	private bfc_min_value_eur: string = "0";
	private bfc_enabled: boolean = false;
	private enable_best2: boolean = false;
	private best2_product_go_live_enabled: boolean = false;
	private aml_active: boolean = false;
	private bfc_upselling_lower_factor: number = 0;
	private bfc_upselling_upper_factor: number = 0;
	private bfc_upselling_limit: string = "";

	// TODO: temp solution - will be corrected with the payments module slicing (in the next 2-3 sprints)
	private _isPaymentOptionsRefreshNeeded: boolean = false;
	public get isPaymentOptionsRefreshNeeded(): boolean {
		return this._isPaymentOptionsRefreshNeeded;
	}

	public constructor(
		private apicallService: ApicallService,
		private infoService: InfoService,
		private translationService: TranslationService,
		private fiatService: FiatService,
		private initService: InitService,
		private darkModeService: DarkModeService
	) {}

	public setMasterData(lang: string): Observable<boolean> {
		if (!this.initService.didInit([this.name])) {
			return this.initMasterdata(lang).pipe(
				map(
					(ret) => {
						const data = ret["attributes"];
						this._languages = parseList(data["languages"], Language);
						this.setLanguageBpOptions(lang);
						this.initService.init(this.name);

						return true;
					},
					(error) => {
						console.error("masterdata failed", error);
						return false;
					}
				)
			);
		}

		observableOf(false);
	}

	public get minBestAmount(): number {
		// FIXME: Hardcoded for now, will be moved to an API-Field soon
		return BFC_MIN_AMOUNT;
	}

	public get account_bfc_deduction(): number {
		return this.bfc_deduction;
	}

	public getBfcGlobalSetting(): boolean {
		return this.bfc_enabled;
	}

	public isBest2Enabled(): boolean {
		return this.enable_best2;
	}

	public isBest2ProductLive(): boolean {
		return this.best2_product_go_live_enabled;
	}

	public get accountBfcMinFeeEur(): string {
		return this.bfc_min_value_eur;
	}

	public isSepaProvider(provider: Provider): boolean {
		return provider.isShortNameSepa();
	}

	public get accountUpsellingUpperFactor(): number {
		return this.bfc_upselling_upper_factor;
	}

	public get accountUpsellingLowerFactor(): number {
		return this.bfc_upselling_lower_factor;
	}

	public get accountUpsellingLimit(): string {
		return this.bfc_upselling_limit;
	}

	public getById<T extends Provider | Country>(id: string, list: T[]): T {
		if (list) {
			for (const item of list) {
				if (item.getId() === id) {
					return item;
				}
			}
			return list[0];
		}
		return null;
	}

	public getPaymentOptionById(id: string): PaymentOption {
		return this._payment_options.find((po: PaymentOption) => po.getId() === id);
	}

	public getPaymentOptionByShortName(shortName: string): PaymentOption {
		return this._payment_options.find((po: PaymentOption) => po.getShortName() === shortName);
	}

	public setPayoutProviders(providers: PayoutProvider[]): void {
		this._payout_providers = providers;
	}

	public setPaymentOptions(options: PaymentOption[]): void {
		this._payment_options = options;
	}

	public getPayoutProviderById(id: string): PayoutProvider {
		return this.payout_providers.find((provider: PayoutProvider) => provider.getId() === id);
	}

	public get payment_options(): PaymentOption[] {
		return this._payment_options;
	}

	public get payout_providers(): PayoutProvider[] {
		return this._payout_providers;
	}

	public get languages(): Language[] {
		return this._languages;
	}

	public get countries(): Country[] {
		return this._countries.filter((c) => c.isUsable());
	}

	public get citizenshipCountries(): Country[] {
		return this._citizenshipCountries;
	}

	public getAllCountries(): Country[] {
		return this._countries;
	}

	public logoUrl(p: PayoutAccount | Provider): string {
		if (p == null) {
			return "";
		}
		let logoUrl = "";
		let id = p.getId();

		if (p instanceof PayoutAccount) {
			id = p.getPayoutProviderId();
		}

		if (p instanceof PayoutAccount || p instanceof Provider) {
			let list: PaymentOption[] | PayoutProvider[] = this.payment_options;

			if (p instanceof PayoutAccount || p instanceof PayoutProvider) {
				list = this.payout_providers;
			}

			const provider = this.getById<Provider>(id, list);
			logoUrl = provider.getLogo(this.darkModeService.darkModeEnabled);
		}

		return logoUrl;
	}

	public getPayoutByFiat(fiatId: string): PayoutProvider[] {
		const pFiat = [];
		for (const p of this.payout_providers) {
			if (p.getFiatIDs().indexOf(fiatId) >= 0) {
				pFiat.push(p);
			}
		}
		return pFiat;
	}

	public getDefaultPaymentByFiat(fiatId: string): Provider {
		const options = this.getPaymentByFiat(fiatId).filter(
			(payment: PaymentOption) => !!payment.isEnabled() && !payment.isUnavailable()
		);
		if (options.length > 0) {
			let p: Provider = null;
			for (const option of options) {
				const fiatDetail = option.getFiatProviderDetails().find((detail: Fee) => detail.getFiatId() === fiatId);
				if (!!fiatDetail && fiatDetail.getInfoAvailable()) {
					p = option;
				}
			}
			if (p) {
				return p;
			}
		}
		return null;
	}

	public getPaymentByFiat(fiatId: string): PaymentOption[] {
		const pFiat = [];
		for (const p of this.payment_options) {
			if (p.getFiatIDs().indexOf(fiatId) >= 0) {
				pFiat.push(p);
			}
		}
		return pFiat;
	}

	public euroToDefault(amount: number, defaultf: Fiat): number {
		return amount / defaultf.toEuroRate();
	}

	private _mapFeesToFiats(fees: Fee[]): Fiat[] {
		return this.fiatService.list.filter((fiat: Fiat) => fees.some((fee: Fee) => fee.getFiatId() === fiat.getId()));
	}

	public getAvailableFiats(p: Provider): Fiat[] {
		const fees: Fee[] = p.getFees();
		return this._mapFeesToFiats(fees);
	}

	private convertLimit(limitList: any, providerlist: Provider[]): void {
		limitList.forEach((a) => {
			const provider = this.getById(a["id"], providerlist);
			if (a["attributes"].length === 0) {
				provider.setLimits([]);
			} else {
				const ll = parseList(a["attributes"]["fiats"], FiatLimit);
				provider.setLimits(ll);
			}
		});
	}

	public getSepaForFiatId(
		fiatId: string,
		paymentOptionId: string,
		userId: string,
		amount: number,
		sendEmail: boolean = false
	): void {
		const url =
			"users/" +
			userId +
			"/payment-options/" +
			paymentOptionId +
			"/fiats/" +
			fiatId +
			"/information?amount=" +
			amount +
			(sendEmail ? "&email=1" : "");
		this.apicallService.get(url, new ApicallOption(Sepa)).subscribe(
			(sepa: Sepa[]) => {
				const list = this.sepas.getValue();
				const index = list.indexOf(list.find((s: Sepa) => s.getId() === fiatId));
				if (index >= 0) {
					list.splice(index, 1);
				}
				list.push(sepa[0]);
				this.sepas.next(list);
				if (sendEmail) {
					this.infoService.setSuccess(this.translationService.translate("deposit_email_successful_send"));
				}
			},
			(error) => Helper.handleError(error)
		);
	}

	public getSepas(): Sepa[] {
		return this.sepas.getValue();
	}

	public getSepas$(): Observable<Sepa[]> {
		return this.sepas.asObservable();
	}

	public get isAmlActive(): boolean {
		return this.aml_active;
	}

	public set isAmlActive(isActive: boolean) {
		this.aml_active = isActive;
	}

	public getCountryById(country_id: string): Country {
		return this.getById(country_id, this.getAllCountries());
	}

	public getCountryByIso(iso: string): Country {
		return this.getAllCountries().find((c: Country) => c.getIso().toUpperCase() === iso.toUpperCase());
	}

	public getCountryByName(name: string): Country {
		return this.getAllCountries().find((c: Country) => c.getName().toLowerCase() === name.toLowerCase());
	}

	public callAllCitizenshipCountries$(): Observable<BpSelectOption[]> {
		const option = new ApicallOption();
		option.apiVersion = "v2";
		return this.apicallService.get("countries?accepted_docs=true", option).pipe(
			map((countries) => this.setCitizenshipCountriesDeserialized(parseList(countries, Country))),
			catchError((error) => {
				Helper.handleError(error);
				return observableOf([]);
			})
		);
	}

	public checkLimits(): void {
		if (this.payment_options.length > 0 && this.payout_providers.length > 0) {
			// Check if limits exist already
			if (
				this.payment_options.some((o: PaymentOption) => o.getLimits().length > 0) ||
				this.payout_providers.some((p: PayoutProvider) => p.getLimits().length > 0)
			) {
				return;
			}
			this.apicallService.get("user/limits").subscribe((a) => {
				console.info("[DEBUG] caught limits");
				const limits = a["attributes"];
				this.convertLimit(limits["payment_option_limits"], this._payment_options);
				this.convertLimit(limits["payout_provider_limits"], this._payout_providers);
			});
		}
	}

	public initMasterdata(lang: string): Observable<any[]> {
		const option = new ApicallOption();
		option.apiVersion = "v4";
		return this.apicallService.get("masterdata", option).pipe(
			map((response) => {
				this.processMasterdata(response["attributes"], lang);
				return response;
			})
		);
	}

	public processMasterdata(data: any, lang: string): void {
		Helper.setLanguage(lang); // To be safe for the parser
		this.setLanguageBpOptions(lang);
		this.setCountries(parseList(data["countries"], Country));

		if (data["languages"]) {
			this._languages = parseList(data["languages"], Language);
		}

		if (data["aml"]) {
			const amlData = parseList(data["aml"], AmlContainer)[0];
			this.isAmlActive = amlData.aml_active;
		}

		if (data["bfc"]) {
			const bfcData = parseList(data["bfc"], BfcContainer)[0];
			this.bfc_deduction = bfcData.bfc_deduction;
			this.bfc_min_value_eur = bfcData.bfc_min_value_eur;
			this.bfc_enabled = bfcData.bfc_enabled;
			this.enable_best2 = bfcData.enable_best2;
			this.best2_product_go_live_enabled = bfcData.best2_product_go_live_enabled;
			this.bfc_upselling_lower_factor = bfcData.bfc_upselling_best_recommended_lower_factor;
			this.bfc_upselling_upper_factor = bfcData.bfc_upselling_best_recommended_upper_factor;
			this.bfc_upselling_limit = bfcData.bfc_upselling_lower_limit;
		}
	}

	public getAllCountriesBpSelectOption(): BpSelectOption[] {
		return this.countryBpOptions;
	}

	public getCountryISO(countryId): string {
		const country: Country = this.countries.find((c: Country) => c.getId() === countryId);
		return country.getIso();
	}

	public getCitizenshipCountryISO(countryId): string {
		const country: Country = this.citizenshipCountries.find((c: Country) => c.getId() === countryId);
		return country?.getIso() ?? "";
	}

	public languagesAsBpSelectOption(): BpSelectOption[] {
		return this.languagesBpOptions;
	}

	private setCountries(countries: Country[]): void {
		this._countries = [...countries];
		this.sortCountries(this._countries);

		this.countryBpOptions = this._countries.map((country) => this.setCountryData(country));
	}

	private setCitizenshipCountriesDeserialized(countries: Country[]): BpSelectOption[] {
		this._citizenshipCountries = [...countries];
		this.sortCountries(this._citizenshipCountries);

		return countries.map((country) => {
			return this.setCountryData(country);
		});
	}

	private setCountryData(country: Country): { name: string; icon: string; value: string } {
		const countryData = {
			name: country.getI18Nname(),
			icon: "flag-icon flag-icon-" + country.getIso().toLowerCase(),
			value: country.getId()
		};
		return countryData;
	}

	private sortCountries(countries: Country[]): void {
		TOP_COUNTRIES_LIST.forEach((iso: string) => {
			const index = countries.findIndex((country: Country) => country.getIso() === iso);
			countries.unshift(countries.splice(index, 1)[0]);
		});
	}

	private setLanguageBpOptions(lang: string): void {
		this.languagesBpOptions = this.languages
			.filter((l: Language) => l.isEnabled() && l.getId() !== "esp")
			.map((language: Language) => ({
				name: language.getMultiLangField("name", lang),
				value: language.getId()
			}));
	}

	public setProviderFromData(payoutProviders: PayoutProvider[], paymentOptions: PaymentOption[]): void {
		const lang = this.translationService.getLanguage();

		const providers = parseList(payoutProviders, PayoutProvider, false, lang).sort((p1, p2) => p1.sort - p2.sort);
		this.setPayoutProviders(providers);

		const options = parseList(paymentOptions, PaymentOption).sort((p1, p2) => p1.sort - p2.sort);
		this.setPaymentOptions(options);
	}

	public markPaymentOptionsAsOutdated(): void {
		this._isPaymentOptionsRefreshNeeded = true;
	}

	public refreshPaymentOptions(): Observable<PaymentOption[]> {
		return this.apicallService.get("paymentoptions", new ApicallOption(PaymentOption)).pipe(
			map((options: PaymentOption[]) => options.sort((p1, p2) => p1.sort - p2.sort)),
			tap((sortedOptions: PaymentOption[]) => {
				this.setPaymentOptions(sortedOptions);
				this.checkLimits();
				this._isPaymentOptionsRefreshNeeded = false;
			})
		);
	}

	public languageIso(languageId: string = "eng"): string {
		let l = this.languages.find((l) => {
			return l.getId() === languageId;
		});
		if (!l) {
			return "en";
		}
		return l.getIsoCode();
	}
}

class AmlContainer {
	public aml_active = false;
}

class BfcContainer {
	public bfc_deduction: number = 0;
	public bfc_min_value_eur: string = "";
	public bfc_enabled: boolean = false;
	public enable_best2: boolean = false;
	public bfc_upselling_best_recommended_lower_factor: number = 0;
	public bfc_upselling_best_recommended_upper_factor: number = 0;
	public bfc_upselling_lower_limit: string = "";
}
